import InfiniteScroll from 'react-infinite-scroll-component';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { Table } from 'antd';
import { debounce, isNil, noop } from 'lodash-es';
import { InfiniteScrollSpin } from '../InfiniteScrollSpin/InfiniteScrollSpin';
import { TableScrollButtons } from '../TableScrollButtons/TableScrollButtons';
import { StyledInfiniteTableWrapper } from './InfiniteTable.styles';

interface InfiniteTableProps {
  dataSource: any[];
  columns: any[];
  next: () => void;
  hasMore: boolean;
  height: number;
  isLoading: boolean;
}

export const InfiniteTable = (props: InfiniteTableProps): JSX.Element => {
  const { dataSource, columns, next, hasMore, height, isLoading } = props;

  const tableRef = useRef<HTMLDivElement>(null);

  const [canBeScrolledToTheLeft, setCanBeScrolledToTheLeft] = useState(false);
  const [canBeScrolledToTheRight, setCanBeScrolledToTheRight] = useState(false);

  const getHorizontalScrollingElement = (): HTMLElement | null =>
    tableRef.current?.querySelector('.table__infinite-scroll') ?? null;

  const calculateScrollability = (scrollingElement: HTMLElement): void => {
    const { scrollWidth, clientWidth, scrollLeft } = scrollingElement;
    const scrollableWidth = scrollWidth - clientWidth;

    setCanBeScrolledToTheRight(scrollableWidth > 0 && scrollLeft + clientWidth < scrollWidth - 1);
    setCanBeScrolledToTheLeft(scrollableWidth > 0 && scrollLeft > 0);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedHandleInfiniteTableLayoutUpdate = useCallback(
    debounce((): void => {
      const scrollingElement = getHorizontalScrollingElement();

      if (!isNil(scrollingElement)) {
        calculateScrollability(scrollingElement);
      }
    }, 50),
    []
  );

  useLayoutEffect(() => {
    if (!isNil(tableRef.current)) {
      const observedElement = tableRef.current?.querySelector('.table__infinite-scroll');
      const resizeObserver = new ResizeObserver(debouncedHandleInfiniteTableLayoutUpdate);

      if (!isNil(observedElement)) {
        resizeObserver.observe(observedElement);

        return () => resizeObserver.unobserve(observedElement);
      }
    }

    return noop;
  }, [debouncedHandleInfiniteTableLayoutUpdate]);

  useLayoutEffect(() => {
    debouncedHandleInfiniteTableLayoutUpdate();
  }, [canBeScrolledToTheLeft, canBeScrolledToTheRight, debouncedHandleInfiniteTableLayoutUpdate]);

  return (
    <StyledInfiniteTableWrapper ref={tableRef}>
      <InfiniteScroll
        className="table__infinite-scroll"
        dataLength={dataSource.length}
        next={next}
        hasMore={hasMore}
        height={height}
        loader={<InfiniteScrollSpin />}
        style={{ width: '100%' }}
        onScroll={debouncedHandleInfiniteTableLayoutUpdate}
        scrollThreshold={1}
      >
        <Table sticky rowKey="id" dataSource={dataSource} columns={columns} pagination={false} loading={isLoading} />
        <TableScrollButtons
          canBeScrolledToTheLeft={canBeScrolledToTheLeft}
          canBeScrolledToTheRight={canBeScrolledToTheRight}
          wrapperRef={tableRef}
          isInfiniteScrollTable
        />
      </InfiniteScroll>
    </StyledInfiniteTableWrapper>
  );
};
